import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { getLocationsForSelectedUser } from '../../server/server';
import { locationItemImages } from '../../constants/constants';
import JourneySlidePanel from './components/JourneySlidePanel';
import JourneyMapOverlay from './components/JourneyMapOverlay';

interface LocationItem {
	id: string;
	code: string;
	name?: string;
	isFound: boolean;
}

export const IMAGE_RATIO = 4.571451446;
const SCALE_FACTOR = 0.7;

const MAP_SLICES = [
	{ id: '1', source: require('../../assets/images/Journey/Map/Slice1.jpg'), width: 360 },
	{ id: '2', source: require('../../assets/images/Journey/Map/Slice2.jpg'), width: 360 },
	{ id: '3', source: require('../../assets/images/Journey/Map/Slice3.jpg'), width: 360 },
	{ id: '4', source: require('../../assets/images/Journey/Map/Slice4.jpg'), width: 360 },
	{ id: '5', source: require('../../assets/images/Journey/Map/Slice5.jpg'), width: 360 },
	{ id: '6', source: require('../../assets/images/Journey/Map/Slice6.jpg'), width: 360 },
	{ id: '7', source: require('../../assets/images/Journey/Map/Slice7.jpg'), width: 360 },
	{ id: '8', source: require('../../assets/images/Journey/Map/Slice8.jpg'), width: 360 },
	{ id: '9', source: require('../../assets/images/Journey/Map/Slice9.jpg'), width: 360 },
	{ id: '10', source: require('../../assets/images/Journey/Map/Slice10.jpg'), width: 471 },
];
const ORIGINAL_MAP_WIDTH = MAP_SLICES.reduce((sum, slice) => sum + slice.width * SCALE_FACTOR, 0);
const ORIGINAL_MAP_HEIGHT = ORIGINAL_MAP_WIDTH / IMAGE_RATIO;

function UserJourneyPreview({ isOpen, closeModal, userProfile }: { isOpen: boolean; closeModal: () => void; userProfile: any }) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [locations, setLocations] = useState<any[]>([]);
	const [progress, setProgress] = useState<any>(null);
	const [showPanel, setShowPanel] = useState(false);

	const fetchUserLocations = useCallback(async () => {
		if (!userProfile?.id) return;

		setLoading(true);
		setError(null);

		try {
			const resp = await getLocationsForSelectedUser(userProfile.id);

			if (resp.status === 200) {
				setLocations(resp.data.locations || []);
				setProgress(resp.data.progress || null);
			} else {
				setError('Failed to load user plan');
			}
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
			setError(errorMessage);
		} finally {
			setLoading(false);
		}
	}, [userProfile?.id]);

	useEffect(() => {
		if (isOpen) fetchUserLocations();
	}, [isOpen, fetchUserLocations]);

	const handleDragToScroll = (e: React.MouseEvent<HTMLDivElement>) => {
		const container = e.currentTarget;
		let startX = e.pageX - container.offsetLeft;
		let scrollLeft = container.scrollLeft;

		const onMouseMove = (moveEvent: MouseEvent) => {
			const x = moveEvent.pageX - container.offsetLeft;
			const walk = x - startX;
			container.scrollLeft = scrollLeft - walk;
		};

		const onMouseUp = () => {
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
			container.style.cursor = 'grab';
		};

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
		container.style.cursor = 'grabbing';
	};

	const locationItems = useMemo<LocationItem[]>(() => {
		if (!locations || locations.length === 0) return [];

		return locations.filter((location) => Array.isArray(location.items) && location.items.length > 0).flatMap((location) => location.items!);
	}, [locations]);

	return (
		<ReactModal
			isOpen={isOpen}
			onRequestClose={closeModal}
			style={{
				overlay: { zIndex: 7, background: '#00000045' },
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: '80vw',
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal' style={{ paddingLeft: 20, paddingRight: 20 }}>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={() => closeModal()}>
						<CloseModalIcon />
					</div>
				</div>

				{loading && !error && <p>Loading...</p>}
				{error && <p className='flex flex-1 text-xl text-redError'>No Journey yet</p>}

				{!loading && locations && !error && (
					<>
						<div className='relative overflow-hidden rounded-2xl'>
							<div className='space-y-6 overflow-x-auto overflow-y-hidden rounded-2xl scrollbar-display-none' onMouseDown={handleDragToScroll} style={{ cursor: 'grab', height: ORIGINAL_MAP_HEIGHT }}>
								<div className='relative' style={{ height: ORIGINAL_MAP_HEIGHT }}>
									<div className='relative flex' style={{ width: ORIGINAL_MAP_WIDTH, height: ORIGINAL_MAP_HEIGHT }}>
										{MAP_SLICES.map((slice) => (
											<img
												key={slice.id}
												src={slice.source}
												alt=''
												className='h-full object-cover shrink-0 pointer-events-none select-none'
												style={{ width: slice.width * SCALE_FACTOR }}
												draggable={false}
											/>
										))}

										<JourneyMapOverlay locations={locations} mapWidth={ORIGINAL_MAP_WIDTH} mapHeight={ORIGINAL_MAP_HEIGHT} scale={SCALE_FACTOR} />

										<img
											src={require('../../assets/images/Journey/JourneyMash.png')}
											alt=''
											className='absolute top-0 left-0 z-30 pointer-events-none select-none'
											style={{ width: ORIGINAL_MAP_WIDTH, height: '100%' }}
										/>
									</div>
								</div>
							</div>

							<JourneySlidePanel show={showPanel} onClose={() => setShowPanel(false)} progress={progress} locationItems={locationItems} />
						</div>
						<button
							className='absolute top-12 left-8 z-40 bg-[#E0D1EB] px-3 py-1 rounded-full shadow-md flex flex-row items-center justify-center gap-2  border-2 border-[#9472ac] '
							onClick={() => setShowPanel(!showPanel)}
						>
							<img src={require('../../assets/images/Journey/Map.png')} alt='' style={{ width: 35, height: 35 }} />
							{progress?.percentage}%
						</button>
					</>
				)}
			</div>
		</ReactModal>
	);
}

export default UserJourneyPreview;
